import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { authModel, apiAuthModel, authStorage, authUserModel, storageApiKey } from '../../models/auth';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private authSubject: BehaviorSubject<authModel | null>;

    constructor(private http: HttpClient, private router: Router) {
        const authData = localStorage.getItem('auth');
        this.authSubject = new BehaviorSubject<authModel | null>(authData ? this.safeJsonParse(authData) : null);
    }

    public login(username: string, password: string, remember_me: boolean, deviceToken: string, level: string) {
        const body = new HttpParams()
            .set('grant_type', 'password')
            .append('level', level)
            .append('username', username)
            .append('password', password);

        const headers = new HttpHeaders()
            .set('Accept', 'application/json')
            .append('Authorization', this.encodeBasicHeader())
            .append('Content-Type', 'application/x-www-form-urlencoded');

        return this.http.post<any>(`${environment.apiUrl}/api/v1/oauth/token/`, body, { headers })
            .pipe(
                map(user => {
                    // Check if the response contains error fields
                    if (user && typeof user === 'object' && ('status' in user || 'title' in user || 'detail' in user)) {
                        throw { error: user }; // Wrap in error object structure
                    }
                    localStorage.setItem('auth', authStorage.createapiAuth(user, remember_me, deviceToken, username, level));
                    return user;
                }));
    }

    public loginWithRefreshToken(refreshToken: string): Observable<authModel | null> {
        const authData = localStorage.getItem('auth');
        const oldKey: authModel | null = authData ? this.safeJsonParse(authData) : null;
        if (!oldKey) {
            throw new Error('No auth data found');
        }
        const body = new HttpParams()
            .set('grant_type', 'refresh_token')
            .append('refresh_token', refreshToken)
            .append('username', oldKey.username)

        const headers = new HttpHeaders()
            .set('Accept', 'application/json')
            .append('Authorization', this.encodeBasicHeader())
            .append('Content-Type', 'application/x-www-form-urlencoded');

        return this.http.post<apiAuthModel>(`${environment.apiUrl}/api/v1/oauth/token/`, body, { headers })
            .pipe(map(user => {
                const authModel = authStorage.createapiAuth(user, oldKey.save_login || false, oldKey.device_token || '', oldKey.username || '', oldKey.level || '')

                localStorage.setItem('auth', authModel);
                return this.safeJsonParse(authModel);
            }));
    }

    public loginWithApiKey(apiKey: string, username: string, deviceToken: string): Observable<authModel | null> {
        const authData = localStorage.getItem('auth');
        const oldKey: authModel | null = authData ? this.safeJsonParse(authData) : null;
        if (!oldKey) {
            throw new Error('No auth data found');
        }
        const body = new HttpParams()
            .set('grant_type', 'password')
            .append('username', username)
            .append('password', apiKey)
            .append('device_token', deviceToken)
            .append('level', 'key');

        const headers = new HttpHeaders()
            .set('Accept', 'application/json')
            .append('Authorization', this.encodeBasicHeader())
            .append('Content-Type', 'application/x-www-form-urlencoded');

        return this.http.post<apiAuthModel>(`${environment.apiUrl}/api/v1/oauth/token/`, body, { headers })
            .pipe(map(user => {
                const authModel = authStorage.createapiAuth(user, oldKey.save_login || false, oldKey.device_token || '', oldKey.username || '', oldKey.level || '');
                localStorage.setItem('auth', authModel);
                return this.safeJsonParse(authModel);
            }));
    }

    public requestApiKey(deviceToken: string) {
        const body = new HttpParams()
            .set('device_token', deviceToken);

        const headers = new HttpHeaders()
            .set('Accept', 'application/json')
            .append('Content-Type', 'application/x-www-form-urlencoded')
            .append('Authorization', 'Bearer ' + (this.getAuth()?.access_token || ''))

        return this.http.post<any>(`${environment.apiUrl}/api/v1/authentication/key/`, body, { headers })
            .pipe(map(api => {
                localStorage.setItem('apiKey', authStorage.createapiKey(api.data));
                return api;
            }));
    }

    public getAuthUserInformation() {
        const headers = new HttpHeaders()
            .set('Accept', 'application/json')
            .append('Content-Type', 'application/x-www-form-urlencoded')
            .append('Authorization', 'Bearer ' + (this.getAuth()?.access_token || ''))

        return this.http.get<{ data: authUserModel }>(`${environment.apiUrl}/api/v1/authentication/`, { headers })
            .pipe(map(users => {
                return users.data;
            }));
    }

    public updateUserInformation(companyId: number, employeeId: number) {
        const body = new HttpParams()
            .set('companyId', String(companyId))
            .append('employeeId', String(employeeId))

        const headers = new HttpHeaders()
            .set('Accept', 'application/json')
            .append('Authorization', 'Bearer ' + (this.getAuth()?.access_token || ''))

        return this.http.put<any>(`${environment.apiUrl}/api/v1/authentication/`, body, { headers })
            .pipe(map(user => {
                return user['data'];
            }));
    }

    public forgotPassword(username: string, level: string) {
        const body = new HttpParams()
            .set('username', username)
            .append('level', level)

        const headers = new HttpHeaders()
            .set('Accept', 'application/json')
            .append('Authorization', this.encodeBasicHeader())

        return this.http.put<any>(`${environment.apiUrl}/api/v1/authentication/forgot/`, body, { headers })
            .pipe(map(response => {
                return response;
            }));
    }

    public updateAccount(password: string, passwordCurrent: string) {
        const body = new HttpParams()
            .set('password', password)
            .append('passwordCurrent', passwordCurrent)

        const headers = new HttpHeaders()
            .set('Accept', 'application/json')

        return this.http.put<any>(`${environment.apiUrl}/account/v1/account/`, body, { headers })
            .pipe(map(response => {
                return response;
            }));
    }

    public generateDeviceToken(length: number) {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    public checkLoginState() {
        const authData = localStorage.getItem('auth');
        if (authData !== null) {
            const auth: authModel | null = this.safeJsonParse(authData);
            if (!auth) return false;
            const apiKeyData = localStorage.getItem('apiKey');
            const apiKey: storageApiKey | null = apiKeyData ? this.safeJsonParse(apiKeyData) : null;
            if (apiKey && apiKey.api_key !== undefined) {
                if (new Date(apiKey.api_key_expires) > new Date()) {
                    return true;
                } else {
                    this.logout();
                    return false;
                }
            } else {
                if (new Date(auth.refresh_token_expires) > new Date()) {
                    return true;
                } else {
                    this.logout();
                    return false;
                }
            }
        }
        return false
    }

    public checkApiKeyValidity() {
        const apiKeyData = localStorage.getItem('apiKey');
        const apiKey: storageApiKey | null = apiKeyData ? this.safeJsonParse(apiKeyData) : null;
        if (apiKey) {
            const expireDate = new Date(apiKey.api_key_expires)
            const now = new Date()
            if (expireDate > now) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    public checkRefreshTokenValidity() {
        const authData = localStorage.getItem('auth');
        if (!authData) return false;
        const auth: authModel | null = this.safeJsonParse(authData);
        if (!auth) return false;
        let expireDate = new Date(auth.refresh_token_expires);
        expireDate.setMinutes(expireDate.getMinutes() - 5);
        const now = new Date()
        if (expireDate > now) {
            return true;
        } else {
            return false;
        }
    }

    public checkAccessTokenValidity() {
        const authData = localStorage.getItem('auth');
        if (!authData) return false;
        const auth: authModel | null = this.safeJsonParse(authData);
        if (!auth) return false;
        let expireDate = new Date(auth.access_token_expires);
        expireDate.setMinutes(expireDate.getMinutes() - 5);
        const now = new Date()
        if (expireDate > now) {
            return true;
        } else {
            return false;
        }
    }

    public getAuth(): authModel | null {
        const authData = localStorage.getItem('auth');
        return authData ? this.safeJsonParse(authData) : null;
    }

    private encodeBasicHeader() {
        return ('Basic ' + btoa(environment.application + ':' + environment.password));
    }

    private safeJsonParse<T>(jsonString: string): T | null {
        try {
            return JSON.parse(jsonString) as T;
        } catch (e) {
            console.error('Failed to parse JSON:', e);
            return null;
        }
    }

    public logout() {
        localStorage.clear();
        this.router.navigate(['authentication/login']);
    }
}
